import type { CkbfsRef } from "@manifest/schema.ts";
import { SessionCache } from "./cache.ts";

const CACHE_NS = "ckbfs-content";
const CACHE_MAX_BYTES = 25 * 1024 * 1024; // 25 MB — markdown + AVIF images
const cache = new SessionCache(CACHE_NS, CACHE_MAX_BYTES);

const PUBLIC_TESTNET_RPCS = ["https://testnet.ckbapp.dev", "https://testnet.ckb.dev"];

export interface CkbfsReader {
  read(ref: CkbfsRef): Promise<Uint8Array>;
}

let _defaultReader: CkbfsReader | null = null;

async function getDefaultReader(): Promise<CkbfsReader> {
  if (_defaultReader) return _defaultReader;
  // Lazy import keeps the chain client out of the initial bundle
  // for users who never open a doc.
  const { CccCkbfsClient } = await import("@/scripts/lib/ckbfs-client.ts");
  // Browser client is read-only — pass a placeholder key (read() never signs).
  const placeholderKey = `0x${"1".repeat(64)}`;

  let lastErr: unknown;
  for (const rpcUrl of PUBLIC_TESTNET_RPCS) {
    try {
      const client = new CccCkbfsClient({
        privateKey: placeholderKey,
        network: "testnet",
        rpcUrl,
      });
      _defaultReader = client;
      return client;
    } catch (e) {
      lastErr = e;
    }
  }
  throw new Error(`all testnet RPCs failed: ${(lastErr as Error)?.message ?? "unknown"}`);
}

export interface FetchOpts {
  reader?: CkbfsReader; // for tests
  bypassCache?: boolean;
}

export async function fetchDocBytes(ref: CkbfsRef, opts: FetchOpts = {}): Promise<Uint8Array> {
  const cacheKey = `${ref.tx_hash}#${ref.index}`;
  if (!opts.bypassCache) {
    const cached = cache.get(cacheKey);
    if (cached) return cached;
  }
  const reader = opts.reader ?? (await getDefaultReader());
  const bytes = await reader.read(ref);
  cache.set(cacheKey, bytes);
  return bytes;
}
